import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import PageShell from '../components/layout/PageShell';

const ROLES_CLAIM = 'https://raah-portal.com/roles';

const engagementTypes = ['Pre-Sales', 'In-Flight Deal', 'Sold Deal', 'Break-Fix/Post-Sales'];
const products = ['Okta WIC', 'Okta CIS', 'Auth0 (B2C/B2B/B2B2C)', 'Multi-product'];
const useCases = [
  'Workforce IAM', 'CIAM', 'Migration', 'AI Identity', 'Agentic AI Security',
  'Identity Threat Detection', 'IGA (Governance)', 'PAM', 'Other',
];

const raahProducts = [
  {
    id: 'bolt',
    name: 'BOLT',
    tagline: 'Okta migration accelerator tooling',
    desc: 'Pre-built tooling for large-scale Okta migrations. Best for enterprise accounts moving from legacy identity providers.',
    whenToUse: 'Large-scale Okta migrations or CIAM transformations.',
  },
  {
    id: 'watt',
    name: 'WATT',
    tagline: 'Web Access Tracking Terminal',
    desc: 'Spin up a protected REST endpoint backed by your own Node.js in minutes - write the handler, set an auth rule, get a URL. No infrastructure to manage.',
    whenToUse: 'Quickly testing or standing up a temporary REST endpoint.',
  },
  {
    id: 'amp',
    name: 'AMP',
    tagline: 'Application Management Portal',
    desc: 'Bulk entity management, today focused on managing Auth0 applications at scale - including reviewing application configurations against RAAH-recommended configs.',
    whenToUse: 'Bulk-managing Auth0 applications across a tenant, or auditing them against RAAH-recommended configuration.',
  },
];

const API_URL = process.env.REACT_APP_API_URL;

const styles = {
  section: { marginBottom: '28px' },
  label: { fontSize: '13px', fontWeight: '600', marginBottom: '8px', display: 'block' },
  input: {
    width: '100%', padding: '8px 10px', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '13px', marginBottom: '12px',
  },
  textarea: {
    width: '100%', padding: '8px 10px', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '13px', minHeight: '80px', resize: 'vertical',
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  chip: (selected) => ({
    padding: '6px 14px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer',
    border: `1px solid ${selected ? '#fd5f0d' : '#ddd'}`,
    background: selected ? '#fef0e6' : '#fff',
    color: selected ? '#fd5f0d' : '#555',
  }),
  submitBtn: (disabled) => ({
    padding: '10px 24px', background: disabled ? '#fcb185' : '#fd5f0d', color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600',
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  note: { fontSize: '12px', color: '#999', marginTop: '8px' },
  successBanner: {
    display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
    padding: '12px 16px', background: '#e6f4ea', border: '1px solid #a8d5b5',
    borderRadius: '6px', color: '#2d6a4f', fontSize: '13px', marginBottom: '20px',
  },
  errorBanner: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '12px 16px', background: '#fdecea', border: '1px solid #f5c2be',
    borderRadius: '6px', color: '#b00020', fontSize: '13px', marginBottom: '20px',
  },
  productsGrid: { display: 'flex', flexWrap: 'wrap', gap: '14px' },
  productCard: (selected) => ({
    position: 'relative', flex: '1 1 220px', minWidth: '220px',
    border: `1px solid ${selected ? '#fd5f0d' : '#e5e5e5'}`,
    borderRadius: '8px', padding: '16px', paddingRight: '34px',
    background: selected ? '#fef0e6' : '#fff', cursor: 'pointer',
  }),
  productName: { fontSize: '14px', fontWeight: '700', marginBottom: '2px' },
  productTagline: { fontSize: '12px', color: '#888' },
  infoTrigger: { position: 'absolute', top: '10px', right: '10px' },
  infoIcon: {
    width: '18px', height: '18px', borderRadius: '50%',
    border: '1px solid #bbb', color: '#888', background: '#fff',
    fontSize: '11px', fontWeight: '700', fontStyle: 'italic', fontFamily: 'Georgia, serif',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'help',
  },
  productInfo: {
    position: 'absolute', top: '24px', right: 0, width: '230px', zIndex: 5,
    background: '#0a0f2c', color: '#fff', borderRadius: '8px',
    padding: '14px', display: 'flex', flexDirection: 'column',
    boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
  },
  productInfoDesc: { fontSize: '12px', lineHeight: 1.5, marginBottom: '8px' },
  productInfoWhen: { fontSize: '11px', color: '#b8c0e8', marginBottom: '10px' },
  productInfoLink: { fontSize: '12px', fontWeight: '600', color: '#ff9d5c', textDecoration: 'none' },
};

// CSS-hover reveal scoped to the "i" trigger (not the whole card, which is now
// clickable for selection) — mouse can move from the icon into the info panel
// without it disappearing, which a JS onMouseEnter/Leave pair can't do cleanly
// since the link needs to stay hoverable/clickable. Injected via a <style> tag
// instead of introducing a separate CSS file for this one section. opacity/
// visibility for the panel live here (not in the inline style object) because
// an inline style attribute always wins over a stylesheet rule regardless of
// selector specificity — setting them inline would permanently defeat :hover.
const productsCSS = `
  .EngageProductInfo {
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease;
  }
  .EngageProductInfoTrigger:hover .EngageProductInfo {
    opacity: 1;
    visibility: visible;
  }
`;

const emptyForm = {
  engagementType: '', product: '', useCase: '', raahProduct: '',
  accountName: '', opportunitySummary: '', timeline: '',
  urgency: '', stakeholders: '', notes: '',
};

function EngageRAAH() {
  const { getAccessTokenSilently, user } = useAuth0();
  const location = useLocation();

  // Coming from Marketplace/Capabilities "Engage RAAH on This" - pre-fill whatever
  // we can confidently match (product/use case chips) and always note the specific
  // thing they clicked, even when it doesn't map to an existing chip.
  const [form, setForm] = useState(() => {
    const ctx = location.state || {};
    const matchedRaahProduct = raahProducts.find(rp => rp.name === ctx.interestLabel);
    return {
      ...emptyForm,
      product: products.includes(ctx.product) ? ctx.product : '',
      useCase: useCases.includes(ctx.useCase) ? ctx.useCase : '',
      raahProduct: matchedRaahProduct ? matchedRaahProduct.name : '',
      notes: ctx.interestLabel ? `Interested in: ${ctx.interestLabel}` : '',
    };
  });
  const [status, setStatus] = useState(null);
  const [feedback, setFeedback] = useState({ message: '', issueUrl: '' });

  const roles = user?.[ROLES_CLAIM] || [];
  const isAdmin = roles.includes('Partner Portal Admin');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setFeedback({ message: '', issueUrl: '' });

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });

      const response = await fetch(`${API_URL}api/engage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Submission failed. Please try again.');
      }

      setStatus('success');
      setFeedback(
        isAdmin
          ? { message: `Request submitted! Work item #${data.sequenceId} created in Plane.`, issueUrl: data.issueUrl }
          : { message: 'Request submitted! A RAAH team member will be assigned shortly.', issueUrl: '' }
      );
      setForm(emptyForm);
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      setStatus('error');
      setFeedback({ message: err.message, issueUrl: '' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isLoading = status === 'loading';

  return (
    <PageShell title="Engage RAAH on a Deal" desc="Submit a request for pre-sales, in-flight, or post-sales support.">
      <style>{productsCSS}</style>

      {status === 'success' && (
        <div style={styles.successBanner}>
          <CheckCircle2 size={15} /> {feedback.message}{' '}
          {feedback.issueUrl && (
            <a href={feedback.issueUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>View in Plane <ArrowRight size={12} /></a>
          )}
        </div>
      )}

      {status === 'error' && (
        <div style={styles.errorBanner}>
          <XCircle size={15} /> {feedback.message}
        </div>
      )}

      <form onSubmit={handleSubmit}>

        <div style={styles.section}>
          <span style={styles.label}>Engagement Type</span>
          <div style={styles.chips}>
            {engagementTypes.map(t => (
              <button type="button" key={t} style={styles.chip(form.engagementType === t)} onClick={() => set('engagementType', t)}>{t}</button>
            ))}
          </div>
        </div>

        <div style={styles.section}>
          <span style={styles.label}>Product Context</span>
          <div style={styles.chips}>
            {products.map(p => (
              <button type="button" key={p} style={styles.chip(form.product === p)} onClick={() => set('product', p)}>{p}</button>
            ))}
          </div>
        </div>

        <div style={styles.section}>
          <span style={styles.label}>Use Case</span>
          <div style={styles.chips}>
            {useCases.map(u => (
              <button type="button" key={u} style={styles.chip(form.useCase === u)} onClick={() => set('useCase', u)}>{u}</button>
            ))}
          </div>
        </div>

        <div style={styles.section}>
          <span style={styles.label}>RAAH Products</span>
          <div style={styles.productsGrid}>
            {raahProducts.map(p => (
              <div
                key={p.id}
                style={styles.productCard(form.raahProduct === p.name)}
                onClick={() => set('raahProduct', form.raahProduct === p.name ? '' : p.name)}
              >
                <div style={styles.productName}>{p.name}</div>
                <div style={styles.productTagline}>{p.tagline}</div>
                <div className="EngageProductInfoTrigger" style={styles.infoTrigger} onClick={e => e.stopPropagation()}>
                  <span style={styles.infoIcon}>i</span>
                  <div className="EngageProductInfo" style={styles.productInfo}>
                    <div style={styles.productInfoDesc}>{p.desc}</div>
                    <div style={styles.productInfoWhen}><strong>When to use:</strong> {p.whenToUse}</div>
                    <Link to="/marketplace" style={{ ...styles.productInfoLink, display: 'inline-flex', alignItems: 'center', gap: '2px' }}>View more in Marketplace <ArrowRight size={12} /></Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.section}>
          <span style={styles.label}>Account Name *</span>
          <input style={styles.input} type="text" placeholder="Customer / account name" value={form.accountName} onChange={e => set('accountName', e.target.value)} required />

          <span style={styles.label}>Opportunity Summary *</span>
          <textarea style={styles.textarea} placeholder="Brief description of the opportunity" value={form.opportunitySummary} onChange={e => set('opportunitySummary', e.target.value)} required />

          <span style={styles.label}>Timeline</span>
          <input style={styles.input} type="text" placeholder="e.g. Close by end of Q2" value={form.timeline} onChange={e => set('timeline', e.target.value)} />

          <span style={styles.label}>Urgency</span>
          <select style={styles.input} value={form.urgency} onChange={e => set('urgency', e.target.value)}>
            <option value="">Select urgency</option>
            <option>Low</option><option>Medium</option><option>High</option><option>Critical</option>
          </select>

          <span style={styles.label}>Stakeholders</span>
          <input style={styles.input} type="text" placeholder="Key contacts or stakeholders" value={form.stakeholders} onChange={e => set('stakeholders', e.target.value)} />

          <span style={styles.label}>Additional Notes</span>
          <textarea style={styles.textarea} placeholder="Anything else the RAAH team should know" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        <button type="submit" style={styles.submitBtn(isLoading)} disabled={isLoading}>
          {isLoading ? 'Submitting…' : 'Submit Request'}
        </button>
        <div style={styles.note}>You'll receive an email confirmation once submitted. A RAAH team member will be assigned shortly.</div>

      </form>
    </PageShell>
  );
}

export default EngageRAAH;